import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type DocumentCategory =
  | "DOCUMENTOS_LEGALES"
  | "RUT"
  | "CAMARA_COMERCIO"
  | "CONTABILIDAD"
  | "IMPUESTOS"
  | "NOMINA"
  | "CONTRATOS"
  | "JURIDICO"
  | "TRAMITES"
  | "REPORTES"
  | "OTROS";

export type DocumentStatus = "ACTIVE" | "ARCHIVED" | "DELETED";

export const DOCUMENT_CATEGORY_LABEL: Record<DocumentCategory, string> = {
  DOCUMENTOS_LEGALES: "Documentos legales",
  RUT: "RUT",
  CAMARA_COMERCIO: "Cámara de comercio",
  CONTABILIDAD: "Contabilidad",
  IMPUESTOS: "Impuestos",
  NOMINA: "Nómina",
  CONTRATOS: "Contratos",
  JURIDICO: "Jurídico",
  TRAMITES: "Trámites",
  REPORTES: "Reportes",
  OTROS: "Otros",
};

export interface ClientDocumentRecord {
  id: string;
  clientId: string;
  category: DocumentCategory;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  version: number;
  replacesDocumentId: string | null;
  documentDate: string | null;
  expirationDate: string | null;
  tags: string[];
  status: DocumentStatus;
  createdAt: string;
  uploadedBy?: { id: string; fullName: string; email: string } | null;
  client?: { id: string; clientType: string; fullName: string | null; businessName: string | null; tradeName: string | null } | null;
}

export interface ListAllDocumentsFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  category?: DocumentCategory;
  search?: string;
}

/** Módulo "Documentos" (continuación posterior a la Fase 26): documentos de todos los clientes de la organización en un solo lugar. */
export function useAllDocuments(filters: ListAllDocumentsFilters = {}) {
  return useQuery({
    queryKey: ["documents", "all", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ClientDocumentRecord[]>>("/documents", { params: filters });
      return data;
    },
  });
}

/** Igual que downloadDocument pero sin exigir clientId — para el módulo global. */
export async function downloadDocumentGlobal(documentId: string): Promise<{ url: string; originalName: string }> {
  const { data } = await apiClient.get<ApiSuccessResponse<{ url: string; originalName: string; mimeType: string }>>(
    `/documents/${documentId}/download`
  );
  return data.data;
}

/**
 * Descarga el binario real del documento a través del propio backend (mismo
 * origen que el frontend) y dispara la descarga en el navegador. A
 * diferencia de downloadDocument/downloadDocumentGlobal (URL firmada de
 * S3/MinIO), en producción MinIO solo es alcanzable dentro de la red interna
 * de Docker — esa URL nunca es alcanzable desde el navegador del usuario, lo
 * que causaba el error al intentar abrir/descargar un documento (ej. Excel).
 * Este endpoint sí funciona porque pasa por el backend, igual que el editor
 * de archivos de Mesa de Trabajo.
 */
async function triggerBrowserDownload(url: string): Promise<void> {
  const { data, headers } = await apiClient.get<ArrayBuffer>(url, { responseType: "arraybuffer" });
  const disposition = (headers?.["content-disposition"] as string | undefined) ?? "";
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : "documento";
  const blob = new Blob([data], { type: (headers?.["content-type"] as string | undefined) ?? "application/octet-stream" });
  const objectUrl = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function downloadDocumentBytes(clientId: string, documentId: string): Promise<void> {
  return triggerBrowserDownload(`/clients/${clientId}/documents/${documentId}/content`);
}

/**
 * Igual que el endpoint que usa downloadDocumentBytes, pero devuelve los
 * bytes crudos en vez de disparar la descarga del navegador — lo usa el
 * editor en el sistema (Excel/Word, el mismo componente que ya usa Mesa de
 * Trabajo) para cargar el documento del cliente sin pasar por una descarga.
 */
export async function fetchDocumentBytes(clientId: string, documentId: string): Promise<ArrayBuffer> {
  const { data } = await apiClient.get<ArrayBuffer>(`/clients/${clientId}/documents/${documentId}/content`, {
    responseType: "arraybuffer",
  });
  return data;
}

export async function downloadDocumentBytesGlobal(documentId: string): Promise<void> {
  return triggerBrowserDownload(`/documents/${documentId}/content`);
}

export interface ListDocumentsFilters {
  page?: number;
  pageSize?: number;
  category?: DocumentCategory;
  search?: string;
}

export function useClientDocuments(clientId: string | undefined, filters: ListDocumentsFilters = {}) {
  return useQuery({
    queryKey: ["clients", clientId, "documents", filters],
    enabled: !!clientId,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ClientDocumentRecord[]>>(
        `/clients/${clientId}/documents`,
        { params: filters }
      );
      return data;
    },
  });
}

export interface UploadDocumentPayload {
  clientId: string;
  file: File;
  category?: DocumentCategory;
  documentDate?: string;
  expirationDate?: string;
  tags?: string; // separados por comas
  replacesDocumentId?: string;
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UploadDocumentPayload) => {
      const formData = new FormData();
      formData.append("file", payload.file);
      if (payload.category) formData.append("category", payload.category);
      if (payload.documentDate) formData.append("documentDate", payload.documentDate);
      if (payload.expirationDate) formData.append("expirationDate", payload.expirationDate);
      if (payload.tags) formData.append("tags", payload.tags);
      if (payload.replacesDocumentId) formData.append("replacesDocumentId", payload.replacesDocumentId);

      const { data } = await apiClient.post<ApiSuccessResponse<ClientDocumentRecord>>(
        `/clients/${payload.clientId}/documents`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.data;
    },
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId, "documents"] }),
  });
}

const PDF_CONVERTIBLE_MIME_TYPES = new Set([
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export function isPdfConvertible(mimeType: string): boolean {
  return PDF_CONVERTIBLE_MIME_TYPES.has(mimeType);
}

/** Convierte un Word/Excel a PDF real (LibreOffice en el servidor) y lo guarda como documento nuevo del expediente. */
export function useConvertDocumentToPdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, documentId }: { clientId: string; documentId: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ClientDocumentRecord>>(
        `/clients/${clientId}/documents/${documentId}/convert-pdf`
      );
      return data.data;
    },
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId, "documents"] }),
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, documentId }: { clientId: string; documentId: string }) => {
      await apiClient.delete(`/clients/${clientId}/documents/${documentId}`);
    },
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId, "documents"] }),
  });
}

/**
 * Obtiene una URL de descarga firmada (expira en minutos) y abre el archivo
 * en una nueva pestaña. Si el almacenamiento no está configurado, la API
 * responde 503 "Integración no configurada" (nunca finge un enlace válido).
 */
export async function downloadDocument(clientId: string, documentId: string): Promise<{ url: string; originalName: string }> {
  const { data } = await apiClient.get<ApiSuccessResponse<{ url: string; originalName: string; mimeType: string }>>(
    `/clients/${clientId}/documents/${documentId}/download`
  );
  return data.data;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
